// Fetch de las plantillas del modo Plantillas de **Belleza y Cuidado Personal**
// — ADR-2026-09-10-plantillas-belleza-modo-propio.
//
// Es un fetch aparte del de Moda (`templatesApi.ts`) y no una variante suya: son otras
// plantillas y otro orden —la curaduría de `CATEGORIAS_BELLEZA` manda primero—, y las
// dos pantallas pueden diverger sin pisarse. Lo que SÍ comparten es la pregunta que
// decide de quién es cada plantilla (`esPlantillaDeBelleza`): la hacen los dos, uno
// para quedársela y el otro para dejarla afuera, así que ninguna puede aparecer en los
// dos catálogos ni desaparecer de los dos.
//
// Reglas:
//   • Sólo las plantillas DE BELLEZA, y TODAS: las que declaran el rubro en
//     `templates.rubros` y las que viven en una categoría de Belleza (la lista curada
//     `CATEGORIAS_BELLEZA` o cualquier slug terminado en `-belleza`). Las dos señales
//     son la misma pregunta que usa el catálogo de Moda para excluirlas
//     (`esPlantillaDeBelleza`), así que una plantilla cae siempre en uno de los dos
//     catálogos y nunca en los dos — ADR-2026-09-11-la-plantilla-va-al-catalogo-de-su-rubro.
//     Cargar plantillas nuevas para el rubro es sólo cargarlas con su rubro: aparecen
//     acá y desaparecen de Moda sin tocar el repo.
//   • Una plantilla que vive en una categoría de Belleza es de Belleza aunque su
//     `rubros` diga otra cosa —manda la curaduría—, y por eso mismo el catálogo de
//     Moda la deja afuera: cada plantilla cae en UN catálogo, nunca en los dos ni en
//     ninguno.
//   • ORDEN: primero las categorías de `CATEGORIAS_BELLEZA` —en ESE orden, que es la
//     curaduría— y después el resto, por el `orden` de la base.
//   • Sólo plantillas activas, de categorías activas.
//   • Globales (`id_company IS NULL`) + las de la empresa del user.
//   • NO hay relleno con categorías de otro rubro: el de `fetchTemplatesV2` —que existe
//     para no dejar la home vacía— acá traería plantillas de Moda, que es exactamente
//     lo que separa a los dos modos.
//   • Categorías vacías no se devuelven.

import { supabase } from '../../lib/supabase';
import type { CollectionV2 } from './mockData';
import {
  COLUMNAS_TEMPLATE,
  mapearColeccion,
  type RawCategoria,
  type RawTemplate,
} from './templatesApi';
import {
  CATEGORIAS_BELLEZA,
  CATEGORIAS_CURADAS,
  esPlantillaDeBelleza,
} from './categoriasCuradas';

// La lista de categorías del modo vive en `categoriasCuradas.ts` y se re-exporta acá
// para que quien trabaje sobre este modo la siga encontrando donde la buscaba. Se
// mudó porque `templatesApi` —el catálogo de Moda— también la necesita, para
// EXCLUIRLA (ADR-2026-09-10-el-catalogo-de-moda-no-toma-categorias-curadas), y
// tenerla acá armaba un import circular entre los dos archivos.
export { CATEGORIAS_BELLEZA } from './categoriasCuradas';

export interface FetchTemplatesBellezaArgs {
  /** id_company del user. null = sin empresa (sólo se ven las globales). */
  companyId: string | null;
}

/**
 * Devuelve las colecciones de Belleza listas para renderizar. El `id` de cada una
 * es el slug de la categoría — el que va en `/interfaz-v2/coleccion-belleza/:slug`.
 */
export async function fetchTemplatesBelleza({
  companyId,
}: FetchTemplatesBellezaArgs): Promise<CollectionV2[]> {
  // Se traen TODAS las categorías activas y TODAS las plantillas visibles, y el
  // reparto se hace en cliente: el criterio ya no es una lista cerrada de slugs sino
  // "la plantilla declara el rubro, o su categoría es de Belleza", y eso no se puede
  // preguntar en la consulta sin un `LIKE` sobre slugs y un `contains` sobre un array,
  // los dos frágiles, sobre un dataset que son unos cientos de filas.
  const { data: categoriasRaw, error: catErr } = await supabase
    .from('template_categorias')
    .select('id, nombre, slug, descripcion, orden')
    .eq('activo', true)
    .order('orden', { ascending: true });
  if (catErr || !categoriasRaw) {
    console.error('[templatesBellezaApi] error fetching categorias', catErr);
    return [];
  }
  const categorias = categoriasRaw as RawCategoria[];
  if (categorias.length === 0) return [];

  let query = supabase
    .from('templates')
    .select(COLUMNAS_TEMPLATE)
    .eq('activo', true)
    .in(
      'categoria_id',
      categorias.map((c) => c.id),
    )
    .order('orden', { ascending: true });
  query = companyId
    ? query.or(`id_company.is.null,id_company.eq.${companyId}`)
    : query.is('id_company', null);
  const { data: templatesRaw, error: tplErr } = await query;
  if (tplErr || !templatesRaw) {
    console.error('[templatesBellezaApi] error fetching templates', tplErr);
    return [];
  }

  // El filtro que decide el catálogo, por PLANTILLA: su rubro o el slug de su
  // categoría. Es la misma pregunta que hace `fetchTemplatesV2` para excluirlas.
  const slugPorCategoria = new Map(
    categorias.map((c) => [c.id, c.slug] as const),
  );
  const templates = (templatesRaw as unknown as RawTemplate[]).filter((t) =>
    esPlantillaDeBelleza(t.rubros, slugPorCategoria.get(t.categoria_id)),
  );

  const porCategoria = new Map<string, RawTemplate[]>();
  for (const t of templates) {
    const arr = porCategoria.get(t.categoria_id) || [];
    arr.push(t);
    porCategoria.set(t.categoria_id, arr);
  }

  // ORDEN: primero las de `CATEGORIAS_BELLEZA` y en ESE orden —la lista es la
  // curaduría, y así se reordena sin tocar Supabase—, y después las demás, en el
  // `orden` que ya trae la consulta. Una categoría nueva aparece al final hasta que
  // alguien decida que va antes, que es la decisión que la lista guarda. Las que no
  // tienen ninguna plantilla de Belleza —todas las de Moda— no llegan a dibujarse: se
  // saltean abajo por venir vacías.
  const porSlug = new Map(categorias.map((c) => [c.slug, c]));
  const enOrden: RawCategoria[] = [];
  for (const slug of CATEGORIAS_BELLEZA) {
    const cat = porSlug.get(slug);
    if (cat) enOrden.push(cat);
  }
  for (const cat of categorias) {
    if (!CATEGORIAS_CURADAS.has(cat.slug)) enOrden.push(cat);
  }

  const collections: CollectionV2[] = [];
  for (const cat of enOrden) {
    const tpls = porCategoria.get(cat.id);
    if (!tpls || tpls.length === 0) continue;
    collections.push(mapearColeccion(cat, tpls));
  }
  return collections;
}
